import { hasPermission } from "@edm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw, Settings, Wrench } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError, apiFetch } from "@/lib/api";
import { useMe } from "@/lib/auth";
import {
  type AssetRow,
  createResource,
  listResource,
  type MaintenanceRow,
} from "@/lib/master-data";
import { formatDateTime } from "@/lib/utils";

const STATUS_LABELS: Record<string, string> = {
  pending_cost_approval: "待成本审批",
  repairing: "维修中",
  completed: "已完成",
  cost_rejected: "成本退回",
  cancelled: "已取消",
};

const STATUS_VARIANT: Record<string, "default" | "success" | "warning" | "danger" | "secondary"> = {
  pending_cost_approval: "warning",
  repairing: "default",
  completed: "success",
  cost_rejected: "danger",
  cancelled: "secondary",
};

function money(cents: number): string {
  return `¥${(cents / 100).toLocaleString("zh-CN", { minimumFractionDigits: 2 })}`;
}

export function MaintenancePage() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [costTarget, setCostTarget] = useState<MaintenanceRow | null>(null);
  const [completeTarget, setCompleteTarget] = useState<MaintenanceRow | null>(null);
  const [cancelTarget, setCancelTarget] = useState<MaintenanceRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deductPartsStock, setDeductPartsStock] = useState(false);
  const [form, setForm] = useState({
    assetId: "",
    description: "",
    cost: "",
    vendor: "",
    expectedAt: "",
  });
  const [costComment, setCostComment] = useState("");
  const [completeForm, setCompleteForm] = useState({
    result: "",
    partName: "",
    partQuantity: "1",
    partCost: "",
  });
  const [cancelReason, setCancelReason] = useState("");

  const canManage = Boolean(me && hasPermission(me.permissions, "operation:manage"));
  const settingsQuery = useQuery({
    queryKey: ["maintenance-settings"],
    queryFn: () => apiFetch<{ deductPartsStock: boolean }>("/api/v1/admin/settings/maintenance"),
    enabled: canManage,
  });
  const canApprove = Boolean(
    me &&
      (hasPermission(me.permissions, "maintenance:approve") ||
        hasPermission(me.permissions, "request:approve_on_behalf")),
  );
  const listQuery = useQuery({
    queryKey: ["maintenance"],
    queryFn: () => listResource<MaintenanceRow>("/api/v1/maintenance"),
  });
  const assetsQuery = useQuery({
    queryKey: ["assets"],
    queryFn: () => listResource<AssetRow>("/api/v1/assets"),
  });
  const rows = listQuery.data ?? [];
  const repairable = (assetsQuery.data ?? []).filter((asset) =>
    ["return_pending", "repairing"].includes(asset.status),
  );

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["maintenance"] });
    queryClient.invalidateQueries({ queryKey: ["assets"] });
  }

  async function createTicket() {
    if (!form.assetId) return toast.error("请选择资产");
    if (!form.description.trim()) return toast.error("请描述维修事项");
    setBusy(true);
    try {
      await createResource("/api/v1/maintenance", {
        assetId: form.assetId,
        description: form.description,
        costYuan: Number(form.cost || 0),
        vendor: form.vendor || null,
        expectedAt: form.expectedAt ? new Date(form.expectedAt).toISOString() : null,
      });
      toast.success("维修单已创建");
      setCreateOpen(false);
      setForm({ assetId: "", description: "", cost: "", vendor: "", expectedAt: "" });
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "创建失败");
    } finally {
      setBusy(false);
    }
  }

  async function decideCost(decision: "approve" | "reject") {
    if (!costTarget) return;
    setBusy(true);
    try {
      await createResource(`/api/v1/maintenance/${costTarget.id}/cost-decision`, {
        decision,
        comment: costComment || null,
      });
      toast.success(decision === "approve" ? "成本已批准" : "成本已退回");
      setCostTarget(null);
      setCostComment("");
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "审批失败");
    } finally {
      setBusy(false);
    }
  }

  async function complete() {
    if (!completeTarget) return;
    if (!completeForm.result.trim()) return toast.error("请填写维修结果");
    setBusy(true);
    try {
      await createResource(`/api/v1/maintenance/${completeTarget.id}/complete`, {
        result: completeForm.result,
        parts: completeForm.partName.trim()
          ? [
              {
                partName: completeForm.partName,
                quantity: Number(completeForm.partQuantity),
                unitCostYuan: Number(completeForm.partCost || 0),
              },
            ]
          : [],
      });
      toast.success("维修已完成，资产回到在库");
      setCompleteTarget(null);
      setCompleteForm({ result: "", partName: "", partQuantity: "1", partCost: "" });
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "完成失败");
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (!cancelTarget) return;
    if (!cancelReason.trim()) return toast.error("请填写取消原因");
    setBusy(true);
    try {
      await createResource(`/api/v1/maintenance/${cancelTarget.id}/cancel`, {
        reason: cancelReason,
      });
      toast.success("维修单已取消");
      setCancelTarget(null);
      setCancelReason("");
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "取消失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="维修维保"
        description="资产先进入归还待检/维修中，再登记维修；成本大于 0 由成本中心负责人审批，完成后资产回到在库。"
        actions={
          <>
            <Button variant="outline" onClick={() => listQuery.refetch()}>
              <RefreshCw className="h-4 w-4" />
              刷新
            </Button>
            {canManage ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeductPartsStock(settingsQuery.data?.deductPartsStock ?? false);
                    setSettingsOpen(true);
                  }}
                >
                  <Settings className="h-4 w-4" />
                  维修设置
                </Button>
                <Button onClick={() => setCreateOpen(true)}>
                  <Plus className="h-4 w-4" />
                  登记维修
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>维修单</CardTitle>
          <CardDescription>维修成本、供应商、预计完成、结果与配件消耗全程留痕。</CardDescription>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <div className="space-y-2">
              {["m1", "m2", "m3"].map((key) => (
                <Skeleton key={key} className="h-11 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title="暂无维修单" description="资产送修后在此登记维修。" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>维修单号</TableHead>
                  <TableHead>资产</TableHead>
                  <TableHead>故障</TableHead>
                  <TableHead>成本</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>预计完成</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-xs">{row.ticketNo}</TableCell>
                    <TableCell>
                      {row.asset?.assetCode ?? "—"}
                      <span className="ml-2 text-xs text-muted-foreground">
                        {row.material?.name ?? ""}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[240px] truncate">{row.description}</TableCell>
                    <TableCell>{money(row.costCents)}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[row.status] ?? "secondary"}>
                        {STATUS_LABELS[row.status] ?? row.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{formatDateTime(row.expectedAt)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canApprove && row.status === "pending_cost_approval" ? (
                          <Button variant="ghost" size="sm" onClick={() => setCostTarget(row)}>
                            成本审批
                          </Button>
                        ) : null}
                        {canManage && row.status === "repairing" ? (
                          <Button variant="ghost" size="sm" onClick={() => setCompleteTarget(row)}>
                            <Wrench className="h-3.5 w-3.5" />
                            完成
                          </Button>
                        ) : null}
                        {canManage &&
                        ["pending_cost_approval", "repairing", "cost_rejected"].includes(
                          row.status,
                        ) ? (
                          <Button variant="ghost" size="sm" onClick={() => setCancelTarget(row)}>
                            取消
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>维修设置</DialogTitle>
            <DialogDescription>控制完成维修时是否从耗材库存扣减配件。</DialogDescription>
          </DialogHeader>
          <Alert variant="info" title="可配置行为">
            关闭时只记录配件成本；开启后必须为每个配件选择物料，库存不足会阻止完成维修。
          </Alert>
          <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={deductPartsStock}
              onChange={(event) => setDeductPartsStock(event.target.checked)}
            />
            <span>
              完成维修时扣减配件库存
              <span className="block text-xs text-muted-foreground">
                写入 issue 库存流水，引用维修单号。
              </span>
            </span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>
              取消
            </Button>
            <Button
              onClick={async () => {
                try {
                  await apiFetch("/api/v1/admin/settings/maintenance", {
                    method: "PUT",
                    body: JSON.stringify({ deductPartsStock }),
                  });
                  await settingsQuery.refetch();
                  toast.success("维修设置已保存");
                  setSettingsOpen(false);
                } catch (error) {
                  toast.error(error instanceof Error ? error.message : "保存失败");
                }
              }}
            >
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>登记维修</DialogTitle>
            <DialogDescription>
              仅归还待检或维修中的资产可登记；成本大于 0 会自动进入成本审批。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>资产</Label>
              <select
                value={form.assetId}
                onChange={(event) => setForm({ ...form, assetId: event.target.value })}
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="">请选择归还待检/维修中资产</option>
                {repairable.map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    {asset.assetCode} · {asset.serialNumber ?? ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>故障/维修事项</Label>
              <Input
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>维修成本（元）</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.cost}
                  onChange={(event) => setForm({ ...form, cost: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>供应商</Label>
                <Input
                  value={form.vendor}
                  onChange={(event) => setForm({ ...form, vendor: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>预计完成</Label>
                <Input
                  type="datetime-local"
                  value={form.expectedAt}
                  onChange={(event) => setForm({ ...form, expectedAt: event.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              取消
            </Button>
            <Button onClick={createTicket} disabled={busy}>
              创建维修单
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(costTarget)} onOpenChange={(open) => !open && setCostTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>维修成本审批：{costTarget?.ticketNo}</DialogTitle>
            <DialogDescription>
              成本 {money(costTarget?.costCents ?? 0)}
              ；审批人由成本中心负责人承担，系统管理员可代批。
            </DialogDescription>
          </DialogHeader>
          <Input
            value={costComment}
            onChange={(event) => setCostComment(event.target.value)}
            placeholder="审批意见"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCostTarget(null)}>
              取消
            </Button>
            <Button variant="danger" onClick={() => decideCost("reject")} disabled={busy}>
              退回
            </Button>
            <Button onClick={() => decideCost("approve")} disabled={busy}>
              批准
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(completeTarget)}
        onOpenChange={(open) => !open && setCompleteTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>完成维修：{completeTarget?.ticketNo}</DialogTitle>
            <DialogDescription>
              完成后资产自动回到在库，使用人清空，需重新办理交接。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>维修结果</Label>
              <Input
                value={completeForm.result}
                onChange={(event) =>
                  setCompleteForm({ ...completeForm, result: event.target.value })
                }
              />
            </div>
            <div className="grid gap-2 sm:grid-cols-3">
              <div className="space-y-2">
                <Label>配件名称</Label>
                <Input
                  value={completeForm.partName}
                  onChange={(event) =>
                    setCompleteForm({ ...completeForm, partName: event.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>数量</Label>
                <Input
                  type="number"
                  min="1"
                  value={completeForm.partQuantity}
                  onChange={(event) =>
                    setCompleteForm({ ...completeForm, partQuantity: event.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>单价（元）</Label>
                <Input
                  type="number"
                  min="0"
                  value={completeForm.partCost}
                  onChange={(event) =>
                    setCompleteForm({ ...completeForm, partCost: event.target.value })
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteTarget(null)}>
              取消
            </Button>
            <Button onClick={complete} disabled={busy}>
              确认完成
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(cancelTarget)} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>取消维修单：{cancelTarget?.ticketNo}</DialogTitle>
            <DialogDescription>取消后维修中资产会回到在库。</DialogDescription>
          </DialogHeader>
          <Input
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
            placeholder="取消原因"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)}>
              取消
            </Button>
            <Button variant="danger" onClick={cancel} disabled={busy}>
              确认取消
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
